import { useEffect, useMemo, useState } from "react";
import { Download, Smartphone, Share2 } from "lucide-react";

// Generates a per-shop manifest at runtime so that "Add to Home Screen"
// installs ONLY this shop's booking page — not the whole AIBox app.
//
// How it works:
// 1. Builds a JSON manifest with name = shop.name, start_url = /s/<slug>/book
//    and a gold-on-black icon (SVG data-URI, scales perfectly).
// 2. Creates a Blob URL for the manifest.
// 3. Swaps the existing <link rel="manifest"> href to the Blob URL.
// 4. The browser's beforeinstallprompt now uses these per-shop fields.

function svgIconDataUri(letter, brandColor) {
  const safe = (letter || "A").toUpperCase().slice(0, 1);
  const c = (brandColor || "#E6C662").replace("#", "%23");
  // 512x512 square — Chrome auto-handles masking.
  return (
    `data:image/svg+xml;utf8,` +
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'>` +
    `<rect width='512' height='512' rx='112' fill='%23050505'/>` +
    `<rect x='32' y='32' width='448' height='448' rx='88' fill='none' stroke='${c}' stroke-width='6' opacity='0.55'/>` +
    `<text x='50%25' y='52%25' dominant-baseline='middle' text-anchor='middle' ` +
    `font-family='Georgia,serif' font-size='280' font-weight='700' font-style='italic' fill='${c}'>` +
    `${safe}</text></svg>`
  );
}

function buildShopManifest(shop) {
  const slug = shop.slug;
  const name = shop.name || "Queue";
  const shortName = name.length > 18 ? name.slice(0, 18) : name;
  const brand = shop.brand_color || "#E6C662";
  const icon = svgIconDataUri(name[0], brand);
  return {
    name,
    short_name: shortName,
    description: `${name} — Book your token. Skip the wait.`,
    start_url: `/s/${slug}/book`,
    scope: `/s/${slug}/`,
    display: "standalone",
    orientation: "portrait",
    background_color: "#050505",
    theme_color: brand,
    icons: [
      { src: icon, type: "image/svg+xml", sizes: "192x192", purpose: "any maskable" },
      { src: icon, type: "image/svg+xml", sizes: "512x512", purpose: "any maskable" },
    ],
  };
}

function installPerShopManifest(shop) {
  try {
    const manifest = buildShopManifest(shop);
    const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
    const url = URL.createObjectURL(blob);
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    // Revoke the previous Blob URL if we created one earlier
    const prev = link.getAttribute("data-blob");
    if (prev) {
      try { URL.revokeObjectURL(prev); } catch {/* ignore */}
    }
    link.href = url;
    link.setAttribute("data-blob", url);

    // Also update apple-touch icons for iOS Home-Screen shortcuts.
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.setAttribute("content", manifest.short_name);
    return true;
  } catch (err) {
    console.warn("[AIBox] manifest install failed:", err);
    return false;
  }
}

export default function PWAInstallButton({ shop, label }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const isIOS = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent;
    const ios = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
    const standalone =
      window.navigator.standalone || window.matchMedia("(display-mode: standalone)").matches;
    return ios && !standalone;
  }, []);
  const isStandalone = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.navigator.standalone || window.matchMedia("(display-mode: standalone)").matches
    );
  }, []);

  // Swap manifest to the per-shop one ASAP so beforeinstallprompt picks it up.
  useEffect(() => {
    if (!shop?.slug) return;
    installPerShopManifest(shop);
  }, [shop]);

  useEffect(() => {
    function onPrompt(e) {
      e.preventDefault();
      setDeferred(e);
    }
    function onInstalled() {
      setInstalled(true);
      setDeferred(null);
    }
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || isStandalone) return null;

  async function install() {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    } else if (isIOS) {
      setShowIOSHelp((v) => !v);
    } else {
      // No prompt captured yet on this browser — show iOS-style hint as a fallback
      setShowIOSHelp((v) => !v);
    }
  }

  const buttonLabel = label || `Install ${shop?.name || "App"}`;

  return (
    <div className="space-y-3">
      <button
        onClick={install}
        data-testid="pwa-install"
        className="w-full rounded-2xl border-2 border-gold/40 bg-gold/10 hover:bg-gold/20 hover:border-gold/70 transition-all px-4 py-3.5 flex items-center justify-between group"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-void border border-gold/50 flex items-center justify-center gold-glow">
            <Smartphone className="w-5 h-5 text-gold" strokeWidth={1.5} />
          </div>
          <div className="text-left">
            <div className="font-body text-base text-white">{buttonLabel}</div>
            <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-gold/80 mt-0.5">
              Add to Home Screen · No app store
            </div>
          </div>
        </div>
        {isIOS ? (
          <Share2
            className="w-5 h-5 text-gold group-hover:translate-x-0.5 transition-transform"
            strokeWidth={1.5}
          />
        ) : (
          <Download
            className="w-5 h-5 text-gold group-hover:translate-y-0.5 transition-transform"
            strokeWidth={1.5}
          />
        )}
      </button>

      {showIOSHelp && (
        <div
          className="rounded-xl border border-gold/30 bg-void/80 p-4 text-sm text-white/85 space-y-2"
          data-testid="pwa-ios-help"
        >
          <div className="font-numbers text-[11px] tracking-[0.3em] uppercase text-gold">
            How to install on iPhone / iPad
          </div>
          <ol className="list-decimal pl-5 space-y-1 text-white/80">
            <li>
              Tap the <span className="font-semibold text-gold">Share</span> button in
              Safari (square with up-arrow).
            </li>
            <li>
              Scroll and choose{" "}
              <span className="font-semibold text-gold">&quot;Add to Home Screen&quot;</span>.
            </li>
            <li>
              Tap <span className="font-semibold text-gold">Add</span> — the icon will open
              this shop&apos;s booking page directly.
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}
